import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useCrashTrackEventReportQuery } from "../../api/network/adminApiServices";
import svgIcons from "../../assets/svg/svgIcons";
import AdminTable from "../../components/adminTable";
import { TAdminTableRowData } from "../../components/types";
import { useAccountTimezone } from "../../hooks/timezone";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
} from "../../utils/dateTime";
import { convertKmphTo } from "../../utils/distance";
import { useLoggedInUserData } from "../../utils/user";
import {
  SPEED_UNIT_VALUES,
  SPEED_UNITS,
  SPEED_UNITS_OPTIONS,
} from "../admin-details/account/constants";
import { crashtrack_tab_slugs } from "./crash-track-admin-details";
import { CrashTrackTabParams } from "./type";
import { APP_CONFIG } from "../../constants/constants";

const ScreenCrashTrackTabEventReport = ({
  assetCrashIdAsset,
  assetCrashIdTimestamp,
  printMode = false,
  onLoadComplete,
}: CrashTrackTabParams) => {
  const tab_slug = crashtrack_tab_slugs[7]; // "event_report";
  const { t: tCrashTrackDetails } = useTranslation("translation", {
    keyPrefix: `admins.crashtrack.detailsPage`,
  });
  const { t } = useTranslation("translation", {
    keyPrefix: `admins.crashtrack.detailsPage.tab_${tab_slug}`,
  });
  const hasCalledLoadComplete = useRef(false);

  const columns = [
    t("tableFields.timestamp"),
    t("tableFields.status"),
    t("tableFields.mph"),
    t("tableFields.heading"),
    t("tableFields.address"),
    t("tableFields.speed_limit"),
  ];

  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const { tzForDateFns } = useAccountTimezone();

  const orgSpeedUnit = useLoggedInUserData("speedUnits") as SPEED_UNIT_VALUES;
  const speedUnit: SPEED_UNITS =
    SPEED_UNITS_OPTIONS.find((option) => option.value === orgSpeedUnit)?.unit ||
    SPEED_UNITS_OPTIONS[0].unit;

  const speedUnitLabel =
    SPEED_UNITS_OPTIONS.find((option) => option.unit === speedUnit)?.label ||
    SPEED_UNITS_OPTIONS[0].label;

  const {
    data: dataCrashTrackEventInfo,
    isFetching: isFetchingCrashTrackEventInfo,
    isError: isErrorCrashTrackEventInfo,
  } = useCrashTrackEventReportQuery(
    { assetId: assetCrashIdAsset, timestamp: assetCrashIdTimestamp },
    { skip: !assetCrashIdAsset || !assetCrashIdTimestamp }
  );
  useEffect(() => {
    if (
      printMode &&
      !isFetchingCrashTrackEventInfo &&
      !hasCalledLoadComplete.current
    ) {
      onLoadComplete?.();
      if (APP_CONFIG.DEBUG.ALL)
        console.log(
          "===== CRASH TRACK TAB LOADED",
          tab_slug,
          new Date().getTime()
        );
      hasCalledLoadComplete.current = true;
    }
  }, [printMode, isFetchingCrashTrackEventInfo, onLoadComplete]);

  const tableData: TAdminTableRowData[] = useMemo(() => {
    if (isFetchingCrashTrackEventInfo || isErrorCrashTrackEventInfo) return [];

    const results = dataCrashTrackEventInfo?.results || [];

    return results.map((item: any): TAdminTableRowData => {
      let formattedTimestamp = item.timestamp;
      if (item.timestamp && tzForDateFns) {
        const jsDate = dbTimeToJsTime(parseInt(item.timestamp));
        formattedTimestamp = formattedDateTimeBasedOnTimezone(
          jsDate,
          tzForDateFns
        );
      }
      let displayStatus = item.Status;
      let lowerCaseStatus = (item.Status || "").toLowerCase();
      if (lowerCaseStatus === "in motion" || lowerCaseStatus === "moving") {
        displayStatus = "Driving";
      } else if (lowerCaseStatus === "heading_change") {
        displayStatus = "Heading Change";
      } else if (lowerCaseStatus === "impact event alert!!!!") {
        displayStatus = "Possible Impact";
      }

      const displayMPH = item.MPH
        ? `${Math.round(
            convertKmphTo(speedUnit, parseFloat(item.MPH))
          )} ${speedUnitLabel}`
        : "-";

      const displaySpeedLimit = item["Speed Limit"]
        ? `${Math.round(
            convertKmphTo(speedUnit, parseFloat(item["Speed Limit"]))
          )} ${speedUnitLabel}`
        : "-";

      return {
        navLink: "",
        cellData: [
          <span className="font-semibold">{formattedTimestamp}</span>,
          displayStatus,
          displayMPH,
          Math.round(item.Heading),
          item.Address,
          displaySpeedLimit,
        ],
      };
    });
  }, [
    dataCrashTrackEventInfo,
    isFetchingCrashTrackEventInfo,
    isErrorCrashTrackEventInfo,
    tzForDateFns,
    speedUnit,
    speedUnitLabel,
  ]);

  if (isFetchingCrashTrackEventInfo)
    return (
      <div className="flex-grow flex items-center justify-center min-h-64">
        <img src={svgIcons.LoadingAnimation} alt="" className="size-12" />
      </div>
    );

  if (isErrorCrashTrackEventInfo) {
    return (
      <div className="flex flex-col items-center justify-center min-h-64 text-center">
        <p className="font-bold text-xl text-gray-400 px-8">
          {tCrashTrackDetails("tab_data_fetch_error")}
        </p>
      </div>
    );
  }

  return (
    <div className="printable-landscape-page flex flex-col gap-4 p-4 overflow-hidden">
      <h2 className="font-semibold text-lg">{t("heading")}</h2>
      <AdminTable
        customWrapperClass="overflow-x-auto"
        columns={columns}
        data={tableData}
        isLoading={isFetchingCrashTrackEventInfo}
      />
    </div>
  );
};

export default ScreenCrashTrackTabEventReport;
